/**
 * Utility functions for formatting voting schedule data
 * Expands grouped date ranges and generates display labels
 */

/**
 * Format a date range into a compact subtitle like "Apr 7–May 5"
 */

const WEEKDAY_FORMATTER = new Intl.DateTimeFormat('en-US', { weekday: 'short' });
const MONTH_DAY_FORMATTER = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
});
const MONTH_DAY_YEAR_FORMATTER = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});
const MONTH_DAY_RANGE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
});

function parseDate(value) {
  if (!value) return null;
  const str = typeof value === 'string' ? value : value.toISOString();
  return new Date(`${str.slice(0, 10)}T00:00:00`);
}

function formatDate(date) {
  return date.toISOString().slice(0, 10);
}

function addDays(date, count) {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + count);
  return nextDate;
}

function minutesFromTimeString(value) {
  const [hoursString, minutesString] = value.split(':');
  const hours = Number(hoursString);
  const minutes = Number(minutesString);
  return hours * 60 + minutes;
}

function timeStringFromMinutes(totalMinutes) {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

export function formatTimeLabel(timeString) {
  const [hoursString, minutesString] = timeString.split(':');
  const hours = Number(hoursString);
  const minutes = Number(minutesString);
  const suffix = hours >= 12 ? 'PM' : 'AM';
  const normalizedHour = hours % 12 || 12;

  if (minutes === 0) {
    return `${normalizedHour} ${suffix}`;
  }

  return `${normalizedHour}:${String(minutes).padStart(2, '0')} ${suffix}`;
}

function formatCompactTimeLabel(timeString) {
  const [hoursString, minutesString] = timeString.split(':');
  const hours = Number(hoursString);
  const minutes = Number(minutesString);
  const normalizedHour = hours % 12 || 12;

  if (minutes === 0) {
    return `${normalizedHour}`;
  }

  return `${normalizedHour}:${String(minutes).padStart(2, '0')}`;
}

export function formatVotingDateRange(startDate, endDate) {
  if (!startDate || !endDate) {
    return null;
  }

  const start = parseDate(startDate);
  const end = parseDate(endDate);

  const startMonth = start.getMonth();
  const endMonth = end.getMonth();
  const startYear = start.getFullYear();
  const endYear = end.getFullYear();

  if (startMonth === endMonth && startYear === endYear) {
    return `${MONTH_DAY_RANGE_FORMATTER.format(start)}–${end.getDate()}`;
  }

  return `${MONTH_DAY_RANGE_FORMATTER.format(start)}–${MONTH_DAY_RANGE_FORMATTER.format(end)}`;
}

function normalizeWindowShape(windowItem, index) {
  const dateStart = windowItem.dateStart || windowItem.startDate;
  const dateEnd = windowItem.dateEnd || windowItem.endDate || dateStart;
  const startTime = windowItem.startTime;
  const endTime = windowItem.endTime;
  const type = windowItem.scheduleType || windowItem.type || 'early_voting';

  if (!dateStart || !dateEnd || !startTime || !endTime) {
    return null;
  }

  const label = windowItem.label || (type === 'election_day' ? 'Election day' : 'Voting');

  return {
    id: windowItem.id || `${dateStart}-${dateEnd}-${startTime}-${endTime}-${index}`,
    type,
    scheduleType: type,
    dateStart,
    dateEnd,
    startDate: dateStart,
    endDate: dateEnd,
    startTime,
    endTime,
    label,
    displayStartTime: formatCompactTimeLabel(startTime),
    displayEndTime: formatCompactTimeLabel(endTime),
    sourceKind: windowItem.sourceKind || null,
    sourceUrl: windowItem.sourceUrl || null,
    notes: windowItem.notes || null,
  };
}

export function normalizeScheduleWindows(scheduleWindows = []) {
  return scheduleWindows
    .map((windowItem, index) => normalizeWindowShape(windowItem, index))
    .filter(Boolean)
    .sort((left, right) => {
      const leftDateTime = `${left.dateStart}T${left.startTime}`;
      const rightDateTime = `${right.dateStart}T${right.startTime}`;
      return leftDateTime.localeCompare(rightDateTime);
    });
}

function expandWindowToDays(windowItem) {
  const days = [];
  let currentDate = parseDate(windowItem.dateStart);
  const endDate = parseDate(windowItem.dateEnd);

  while (currentDate <= endDate) {
    days.push({
      id: `${windowItem.id}-${formatDate(currentDate)}`,
      date: formatDate(currentDate),
      startTime: windowItem.startTime,
      endTime: windowItem.endTime,
      type: windowItem.type,
      label: windowItem.label,
      displayStartTime: windowItem.displayStartTime,
      displayEndTime: windowItem.displayEndTime,
    });

    currentDate = addDays(currentDate, 1);
  }

  return days;
}

export function getScheduleBounds(scheduleWindows = []) {
  if (!scheduleWindows.length) {
    return {
      earliestTime: '08:00',
      latestTime: '18:00',
    };
  }

  const allMinutes = scheduleWindows.flatMap((windowItem) => [
    minutesFromTimeString(windowItem.startTime),
    minutesFromTimeString(windowItem.endTime),
  ]);

  const earliestMinutes = Math.min(...allMinutes);
  const latestMinutes = Math.max(...allMinutes);

  const paddedEarliest = Math.floor(earliestMinutes / 30) * 30;
  const paddedLatest = Math.ceil(latestMinutes / 30) * 30;

  return {
    earliestTime: timeStringFromMinutes(paddedEarliest),
    latestTime: timeStringFromMinutes(paddedLatest),
  };
}

export function buildHalfHourSlots(startTime, endTime) {
  const startMinutes = minutesFromTimeString(startTime);
  const endMinutes = minutesFromTimeString(endTime);
  const slots = [];

  for (let current = startMinutes; current < endMinutes; current += 30) {
    slots.push({
      key: `slot-${current}`,
      time: timeStringFromMinutes(current),
      startMinutes: current,
      endMinutes: current + 30,
    });
  }

  return slots;
}

function startOfWeek(date) {
  const dayIndex = date.getDay();
  const mondayOffset = dayIndex === 0 ? -6 : 1 - dayIndex;
  return addDays(date, mondayOffset);
}

function endOfWeek(date) {
  return addDays(startOfWeek(date), 6);
}

export function buildCalendarWeeks(scheduleWindows = []) {
  if (!scheduleWindows.length) {
    return [];
  }

  const expandedDays = scheduleWindows.flatMap(expandWindowToDays);
  const groupedWindowsByDate = new Map();

  expandedDays.forEach((dayWindow) => {
    const currentWindows = groupedWindowsByDate.get(dayWindow.date) || [];
    currentWindows.push(dayWindow);
    groupedWindowsByDate.set(dayWindow.date, currentWindows);
  });

  const firstDate = parseDate(expandedDays[0].date);
  const lastDate = parseDate(expandedDays[expandedDays.length - 1].date);
  let currentWeekStart = startOfWeek(firstDate);
  const finalWeekEnd = endOfWeek(lastDate);
  const weeks = [];

  while (currentWeekStart <= finalWeekEnd) {
    const weekDays = Array.from({ length: 7 }, (_, index) => {
      const currentDate = addDays(currentWeekStart, index);
      const dateKey = formatDate(currentDate);

      return {
        date: dateKey,
        weekdayShort: WEEKDAY_FORMATTER.format(currentDate),
        monthDay: MONTH_DAY_FORMATTER.format(currentDate),
        fullLabel: MONTH_DAY_YEAR_FORMATTER.format(currentDate),
        windows: (groupedWindowsByDate.get(dateKey) || []).sort((left, right) => {
          return minutesFromTimeString(left.startTime) - minutesFromTimeString(right.startTime);
        }),
      };
    });

    weeks.push({
      key: `week-${formatDate(currentWeekStart)}`,
      weekStart: formatDate(currentWeekStart),
      weekEnd: formatDate(addDays(currentWeekStart, 6)),
      weekLabel: `Week of ${MONTH_DAY_RANGE_FORMATTER.format(currentWeekStart)}`,
      days: weekDays,
    });

    currentWeekStart = addDays(currentWeekStart, 7);
  }

  return weeks;
}

export function findDefaultWeekKey(weeks = []) {
  return weeks[0]?.key || null;
}

export function buildWeekJumpLabel(week) {
  return `Week of ${MONTH_DAY_RANGE_FORMATTER.format(parseDate(week.weekStart))}`;
}

export function getScheduleWindowBlockStyle(windowItem, slotRowHeight, visibleStartMinutes = 0) {
  const startMinutes = minutesFromTimeString(windowItem.startTime);
  const endMinutes = minutesFromTimeString(windowItem.endTime);
  const top = ((startMinutes - visibleStartMinutes) / 30) * slotRowHeight;
  const height = Math.max(((endMinutes - startMinutes) / 30) * slotRowHeight, slotRowHeight);

  return {
    top: `${top}px`,
    height: `${height}px`,
  };
}

export function hasVotingWindows(windows = []) {
  return windows.length > 0;
}
